import type { Vocabulary, VocabularyOverride } from './vocabulary.types'
import { base } from './vocabulary.base'
import { logistics } from './vocabulary.logistics'

export type { Vocabulary, VocabularyOverride } from './vocabulary.types'

/**
 * The dashboard's vocabulary — every user-facing string, resolved for the build's vertical. See
 * OPERATOR_PLATFORM_ARCHITECTURE.md "Dashboard composition ... settled 2026-07-26": a vertical
 * dashboard is `{shell} + {picked screens} + {its own screens} + a manifest`, everything baked at
 * build time, zero runtime "which vertical am I" logic.
 *
 * Shape: a NEUTRAL BASE ({@link vocabulary.base.ts}) plus a per-vertical OVERRIDE that names only the
 * strings it changes ({@link vocabulary.logistics.ts}), DEEP-merged. `VITE_VERTICAL` — set by
 * `vite build --mode <vertical>` reading `.env.<vertical>` — selects the override; unset ⇒ the neutral
 * base. The selector is a compile-time literal, so an unused vertical's copy tree-shakes OUT of the
 * bundle: a build ships exactly one vertical's words.
 *
 * Consumers import `vocabulary` and read `vocabulary.<screen>.<key>` (or `vocabulary.terms.*`). No
 * user-facing string is hardcoded in a component — it is data here, overridable per vertical.
 */

/** Deep-merge an override onto the base: objects merge key-by-key, arrays and primitives replace. */
function deepMerge<T>(target: T, override: unknown): T {
  if (override === undefined) return target
  if (target === null || typeof target !== 'object' || Array.isArray(target)) {
    return override as T
  }
  const out: Record<string, unknown> = { ...(target as Record<string, unknown>) }
  for (const key of Object.keys(override as Record<string, unknown>)) {
    out[key] = deepMerge(out[key], (override as Record<string, unknown>)[key])
  }
  return out as T
}

// Compile-time literal ⇒ the neutral build dead-code-eliminates the logistics import.
// Add a vertical: `.env.<name>` with VITE_VERTICAL=<name>, its override module, one more branch here.
const selected: VocabularyOverride | undefined =
  import.meta.env.VITE_VERTICAL === 'logistics' ? logistics : undefined

export const vocabulary: Vocabulary = deepMerge(base, selected)
